<template>
<div data-module="matter-article-body">
  <div class="module-layout">
    <div class="article__body">
      <h1 class="article__title">{{ data.title }}</h1>
      <div class="article__meta">
        <div class="thumbnail"
          :style="thumbStyles"
          ref="thumbnailImage"
        />
        <div>
          <template v-if="isMobile">
          <div class="byline" v-html="byline" />
          <div class="date">{{ data.date | formatDate }}</div>
          </template>
          <template v-else>
          <div class="byline" v-html="byline" />
          <div class="date">{{ data.date | formatDate }}</div>
          </template>
        </div>
      </div>
      <markdown :source="data.body" class="markdown" />
    </div>
  </div>
</div>
</template>

<script>
// Libs
import Vue from 'vue';

// Utils
import { getResponsiveUrl } from '@/utils/image-utils';
import WorkMixin from '@/modules/work/WorkMixin';

export default {

  props: ['data'],

  mixins: [WorkMixin],

  data() {
    return {
      thumbContentWidth: 0,
    };
  },

  computed: {

    isMobile() {
      return this.$store.getters.isMobile;
    },

    person() {
      const { byline } = this.data;
      return this.$store.getters.getPersonInfo(byline);
    },

    thumbStyles() {
      const { person, thumbContentWidth: tWidth } = this;
      const thumbSrc = person ? person.thumbSrc : null;

      const rUrl = thumbSrc ? getResponsiveUrl(thumbSrc, tWidth) : '/static/content/uploads/thumb-person.png';

      const styles = {
        backgroundImage: `url(${rUrl})`,
      };

      return styles;
    },

    byline() {
      const { person, isMobile } = this;
      let byline = '';
      if (person) {
        const parts = [person.title, person.position];
        byline = (isMobile) ? parts.join('<br />') : parts.join(', ');
      }
      return byline;
    },

  },

  methods: {

    measureLayout() {
      this.thumbContentWidth = this.getThumbContentWidth();
    },

    getThumbContentWidth() {
      const el = this.$refs.thumbnailImage;
      return (el) ? el.clientWidth : 0;
    },

  },

  watch: {

    currentWidth(/* newVal, oldVal */) {
      Vue.nextTick(() => {
        this.measureLayout();
      });
    },

  },

  mounted() {
    this.measureLayout();
  },

};
</script>


<style lang="scss" scoped>
@import '~@/styles/_variables.scss';
@import '~@/styles/_mixins.scss';
@import '~@/styles/_grid.scss';
@import '~@/styles/_typography.scss';

$layout--percentage: $layout--max-content-width-ng / $layout--optimal-desktop * 100%;

[data-module="matter-article-body"] {
  width: 100%;

  // media query props
  margin-bottom: rem($layout--default-module-spacing-mobile);

  @include ngMq($nbp--tablet, up) {
    margin-bottom: rem($layout--default-module-spacing-tablet);
  }

  @include ngMq($nbp--desktop, up) {
    margin-bottom: rem($layout--default-module-spacing-desktop);
  }

  .module-layout {

    // media query props
    @include ngGrid('mobile');

    @include ngMq($nbp--tablet, up) {
      @include ngGrid('tablet');
    }

    @include ngMq($nbp--desktop, up) {
      @include ngGrid('desktop');
    }

    .article__body {
      // media query props
      grid-column: 1 / -1;
      margin-left: 0;
      margin-right: 0;

      @include ngMq($nbp--tablet, up) {
        grid-column: 2 / -2;

        margin-left: rem($grid-gap-tablet * -1px); // pull to left, column
        margin-right: rem($grid-gap-tablet * -1px); // pull to right, column
      }
      @include ngMq($nbp--desktop, up) {
        grid-column: 2 / -2;
        margin-left: 0;
        margin-right: 0;
      }


      .article__title {
        // media query props
        @include serif( $size: (35, 45, 0) );
        margin-bottom: rem(42px);

        @include ngMq($nbp--tablet, up) {
          @include serif( $size: (40, 63, 0) );
          margin-bottom: rem(12px);
        }
        @include ngMq($nbp--desktop, up) {
          @include serif( $size: (48, 63, 0) );
          margin-bottom: rem(26px);
        }
      }

      .article__meta {
        @include flexbox();

        // media query props
        grid-column: 1 / -1;
        height: 58px;
        margin-bottom: rem(45px);

        @include ngMq($nbp--tablet, up) {
          grid-column: 2 / -2;
          height: 58px;
          margin-bottom: rem(55px);
        }
        @include ngMq($nbp--desktop, up) {
          grid-column: 3 / -3;
          height: 60px;
          margin-bottom: rem(65px);
        }

        .thumbnail {
          background-repeat: none;
          background-position: center center;
          background-size: cover;

          // media query props
          margin-right: rem(24px);
          width: 58px;
          height: 58px;
          border-radius: 58px;

          @include ngMq($nbp--tablet, up) {
            margin-right: rem(24px);
            width: 58px;
            height: 58px;
            border-radius: 58px;
          }
          @include ngMq($nbp--desktop, up) {
            margin-right: rem(28px);
            width: 60px;
            height: 60px;
            border-radius: 60px;
          }
        }

        .byline,
        .date {
          color: $color--slate;

          // media query props
          @include sansserif((16, 19, 0), $weight: 'light');

          @include ngMq($nbp--tablet, up) {
            @include sansserif((16, 19, 0), $weight: 'light');
          }
          @include ngMq($nbp--desktop, up) {
            @include sansserif((20, 24, 0), $weight: 'light');
          }
        }
      }

      /* markdown overrides */
      .markdown {
        h2 {
          margin-top: 2rem;
        }
      }

    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/matter-article/ArticleBody.vue